import type { ReactNode } from "react";
import { cardMutedTextClass, cardTitleTextClass } from "@/components/common/surfaceClasses";

export function OrgDashboardStatCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className={`rounded-xl border p-4 ${accent ?? `border-gray-100 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-900/40`}`}>
      <div className={`text-xs uppercase tracking-wide ${cardMutedTextClass}`}>{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${cardTitleTextClass}`}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${cardMutedTextClass}`}>{sub}</div>}
    </div>
  );
}

export function OrgDashboardBarChart({
  data,
  days,
}: {
  data: { date: string; total: number; sent: number }[];
  days: number;
}) {
  const max = Math.max(...data.map((d) => d.total), 1);
  const showLabel = (i: number) =>
    days <= 14 || (days <= 30 ? i % 5 === 0 || i === data.length - 1 : i % 13 === 0 || i === data.length - 1);
  return (
    <div>
      <div className="flex items-end gap-0.5 h-16 w-full">
        {data.map((d, i) => {
          const totalH = Math.round((d.total / max) * 100);
          const sentH = Math.round((d.sent / max) * 100);
          return (
            <div
              key={d.date}
              className="flex flex-col items-center flex-1"
              title={`${d.date.slice(5)}: ${d.total} total, ${d.sent} sent`}
            >
              <div className="w-full relative flex flex-col justify-end" style={{ height: "48px" }}>
                <div className="w-full rounded-sm bg-indigo-100 dark:bg-indigo-950/50" style={{ height: `${totalH}%` }} />
                {d.sent > 0 && (
                  <div className="absolute bottom-0 w-full rounded-sm bg-indigo-500" style={{ height: `${sentH}%` }} />
                )}
              </div>
              {showLabel(i) && (
                <div className="text-[9px] text-gray-400 dark:text-zinc-500 mt-0.5 whitespace-nowrap">{d.date.slice(5)}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function OrgDashboardHBar({
  label,
  count,
  max,
  color,
}: {
  label: string;
  count: number;
  max: number;
  color: string;
}) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <div className={`w-28 shrink-0 text-xs truncate ${cardMutedTextClass}`}>{label}</div>
      <div className="flex-1 bg-gray-100 dark:bg-zinc-800 rounded-full h-2 overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className={`w-8 text-right text-xs shrink-0 ${cardMutedTextClass}`}>{count}</div>
    </div>
  );
}

export function OrgDashboardTooltip({ text }: { text: string }) {
  return (
    <span className="relative group inline-flex items-center">
      <span className="cursor-help text-[10px] font-bold text-gray-300 hover:text-gray-500 border border-gray-200 hover:border-gray-400 rounded-full w-3.5 h-3.5 inline-flex items-center justify-center leading-none select-none transition-colors">
        ?
      </span>
      <span className="pointer-events-none absolute left-5 -top-1 z-20 w-60 rounded-lg bg-gray-800 text-white text-[11px] leading-snug px-2.5 py-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg whitespace-normal">
        {text}
      </span>
    </span>
  );
}
